import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUserStore } from "../store/AuthUserStore";
import toast from "react-hot-toast";
import axiosApi from "../server/service";
import VoiceNoteRecorder from "../component/VoiceRecorder";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  Check,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";
import { format } from 'date-fns';

// --- TYPE DEFINITIONS (Matching your new backend structure) ---
type AssignmentType =
  | "masterAssignments"
  | "karigarAssignments"
  | "jobWorkAssignments";

interface IVoiceNote {
  _id: string;
  url: string;
  uploadedAt: string;
  [key: string]: any;
}

interface IAdminNoteGroup {
    _id: string;
    userId: string;
    notes: IVoiceNote[];
}

interface IRemarkEntry {
    _id: string;
    text: string;
    createdAt: string; // ISO string from backend
}

interface ITakaEntry {
  id: string;
  meters: number;
  warehouse: "mumbai" | "surat";
  createdAt: string;
}

interface CompletionSteps {
  fabric: boolean;
  astar: boolean;
  embroidery: boolean;
}

interface IAssignmentData {
  _id: string;
  voiceNotes?: IVoiceNote[];
  workStatus: "in progress" | "incomplete" | "completed";
  remarks?: string;
  completionSteps?: CompletionSteps;
  masterRemark?: IRemarkEntry[]; 
  jobWorkRemark?: IRemarkEntry[];
  karigarRemark?: string; // Placeholder for future generic use
  groupWorkRemark?: string; // Placeholder for future generic use

}

interface OrderDetailData {
  _id: string;
  sizeSegmentId: { _id: string; segmentName: string };
  quantity: number;
  totalPieces: number;
  masterAssignments?: IAssignmentData[];
  karigarAssignments?: IAssignmentData[];
  jobWorkAssignments?: IAssignmentData[];
}

interface IOrderDetails {
  _id: string;
  lotNumber: string;
  totalQuantity: number;
  designId: {
    _id: string;
    basicInfo: { designNo: string };
    images: { [key: string]: { url: string; [key: string]: any } };
  };
  orderDetails: OrderDetailData[];
  takaArrival: ITakaEntry[];
  userEmployeeType?: "Master" | "Karigar" | "job-work";
  adminNotes: IAdminNoteGroup[];
  employeeNotes: IAdminNoteGroup[];
}


// --- MAIN COMPONENT ---
export default function EmployeeDashboard() {
  const user = useUserStore((state) => state.user);
  
  const [order, setOrder] = useState<IOrderDetails | null>(null);
  const [allImages, setAllImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState< "completed" | "incomplete" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);



  // const [masterRemark, setMasterRemark] = useState("");
  // state for generic use (Master or Job Work)
  // const [employeeRemark, setEmployeeRemark] = useState(""); 
  const [newRemarkText, setNewRemarkText] = useState("");
  const [isRemarkSaving, setIsRemarkSaving] = useState(false);

  const [masterSteps, setMasterSteps] = useState<CompletionSteps>({ fabric: false, astar: false, embroidery: false });

  const getAssignmentKey = (type: string | undefined): AssignmentType | null => {
    if (type === "Master") return "masterAssignments";
    if (type === "Karigar") return "karigarAssignments";
    if (type === "job-work") return "jobWorkAssignments";
    return null;
  };

 const getFirstAssignment = (orderData: IOrderDetails | null): IAssignmentData | null => {
    if (!orderData || !orderData.orderDetails || orderData.orderDetails.length === 0) return null;
    const detail = orderData.orderDetails[0];
    const key = getAssignmentKey(orderData.userEmployeeType);
    return key && detail[key] && detail[key]!.length > 0 ? detail[key]![0] : null;
  };

  const handleStatusClick = (status: "completed" | "incomplete") => {
    setActionToConfirm(status);
    setIsConfirmModalOpen(true);
  };

  const handleMasterStepChange = (step: keyof CompletionSteps) => {
    setMasterSteps((prev) => ({ ...prev, [step]: !prev[step] }));
  };

  const fetchData = useCallback(async () => {
    // Set loading true only if there isn't already an order displayed
    if (!order) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await axiosApi.get(`/api/orders/employee-task`);
      const fetchedTasks: IOrderDetails[] = response.data.data;

      if (fetchedTasks.length === 0) {
        setOrder(null);
        setError("You do not have any assigned tasks currently.");
        return;
      }
      
      const mainOrder = fetchedTasks[0];
      setOrder(mainOrder);
      
      const designData = mainOrder.designId;
      const imageFields = ["primaryImage", "additionalImage1", "additionalImage2", "additionalImage3", "additionalImage4"];
      const imageUrls = imageFields.map((field) => designData.images[field]?.url).filter((url): url is string => !!url);
      setAllImages(imageUrls);

    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch task details.");
      setOrder(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, order]); // Added `order` dependency

  useEffect(() => {
    if (user?.id) {
        fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);




//   useEffect(() => {
//   const firstAssignment = getFirstAssignment(order);
//   if (order && firstAssignment) {
//    const type = order.userEmployeeType;
//    if (type === "Master") {
//     setMasterSteps(firstAssignment.completionSteps || { fabric: false, astar: false, embroidery: false });
//     setEmployeeRemark(firstAssignment.masterRemark || "");
//    } else if (type === "job-work") {
//     // Load jobWorkRemark for Job Work
//     setEmployeeRemark(firstAssignment.jobWorkRemark || "");
//    } else {
//     // Karigar or other types: clear or reset state
//     setEmployeeRemark("");
//     setMasterSteps({ fabric: false, astar: false, embroidery: false });
//    }
//   }
//  // eslint-disable-next-line react-hooks/exhaustive-deps
//  }, [order]);


useEffect(() => {
  const firstAssignment = getFirstAssignment(order);
  if (order && firstAssignment) {
   const type = order.userEmployeeType;
   if (type === "Master") {
    setMasterSteps(firstAssignment.completionSteps || { fabric: false, astar: false, embroidery: false });
   } 
      // Reset the input field every time the order changes
      setNewRemarkText(""); 
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [order]);

  
  const handleConfirmAction = async () => {
    if (!order || !order.orderDetails || order.orderDetails.length === 0 || !actionToConfirm) {
        toast.error("Order data is missing. Please refresh.");
        setIsConfirmModalOpen(false);
        return;
    }
    
    const orderId = order._id;
    const firstDetail = order.orderDetails[0];
    const assignmentKey = getAssignmentKey(order.userEmployeeType);
    const firstAssignment = assignmentKey ? firstDetail[assignmentKey]?.[0] : null;

    if (!firstAssignment) {
        toast.error("Could not find your assignment in the order.");
        setIsConfirmModalOpen(false);
        return;
    }

    const orderDetailId = firstDetail._id;
    const assignmentId = firstAssignment._id;
    const payload = { status: actionToConfirm };

    setIsSubmitting(true);
    const toastId = toast.loading(`Marking task as ${actionToConfirm}...`);

    try {
      await axiosApi.patch(`/api/orders/${orderId}/${orderDetailId}/${assignmentId}/update-work-status`, payload);
      toast.success(`Task marked ${actionToConfirm}!`, { id: toastId });
      await fetchData(); 
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update status.", { id: toastId });
    } finally {
      setIsConfirmModalOpen(false);
      setActionToConfirm(null);
      setIsSubmitting(false);
    }
  };

  const handleUpdateMasterProgress = async () => {
    if (!order || !order.orderDetails || order.orderDetails.length === 0) {
        toast.error("Order data is missing. Please refresh.");
        return;
    }

    const orderId = order._id;
    const firstDetail = order.orderDetails[0];
    const firstAssignment = firstDetail.masterAssignments?.[0];

    if (!firstAssignment) {
        toast.error("Could not find your assignment in the order.");
        return;
    }
    
    const orderDetailId = firstDetail._id;
    const assignmentId = firstAssignment._id;
    const payload = { completionSteps: masterSteps };

    setIsSubmitting(true);
    const toastId = toast.loading("Saving your progress...");

    try {
      await axiosApi.patch(`/api/orders/${orderId}/${orderDetailId}/${assignmentId}/update-work-status`, payload);
      
      const wasCompleted = Object.values(masterSteps).every(s => s === true);
      toast.success(wasCompleted ? "Task marked as complete!" : "Progress saved!", { id: toastId });

      // --- THE FIX: Always call fetchData to get complete data ---
      await fetchData();

    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save progress.", { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

      const handleSaveEmployeeRemark = async () => {
        if (!order) {
            toast.error("Order data is missing. Cannot save remark.");
            return;
        }

         if (newRemarkText.trim() === "") {
             toast.error("Remark cannot be empty. Please provide your notes.");
            return;
        }


        const orderId = order._id;
        const firstAssignment = getFirstAssignment(order);

        if (!firstAssignment) {
            toast.error("Could not find your assignment in the order.");
            return;
        }
        
        const assignmentId = firstAssignment._id;
        
         // Generic payload for the new controller
      const payload = {
        employeeType: order.userEmployeeType, // Used by backend to determine field name (masterRemark or jobWorkRemark)
        remarkValue: newRemarkText// Current state value
      }; 

        setIsRemarkSaving(true);
        const toastId = toast.loading("Saving your remark...");

        try {

            const endpoint = `/api/orders/${orderId}/${assignmentId}/update-employee-remark`;
            await axiosApi.patch(endpoint, payload);
            
            toast.success("Remark saved successfully!", { id: toastId });
            setNewRemarkText("");
            await fetchData(); 
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Failed to save remark.", { id: toastId });
        } finally {
            setIsRemarkSaving(false);
        }
    };


  useEffect(() => {
    const firstAssignment = getFirstAssignment(order);
    if (order?.userEmployeeType === "Master" && firstAssignment?.completionSteps) {
      setMasterSteps(firstAssignment.completionSteps);
    }
    // No need for an else clause, it will keep its default state if no order.
  }, [order]);

  const autoplayPlugin = useRef(Autoplay({ delay: 8000, stopOnInteraction: true }));
  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };
  const nextImage = () => setCurrentImageIndex((i) => (i + 1) % allImages.length);
  const prevImage = () => setCurrentImageIndex((i) => (i - 1 + allImages.length) % allImages.length);

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen"><Loader2 className="animate-spin text-sky-600" size={48} /></div>;
  }
  if (error || !order) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-center p-4">
        <CheckCircle className="text-green-500 h-16 w-16 mb-4" />
        <h2 className="text-2xl font-bold text-slate-700">All Clear!</h2>
        <p className="text-slate-500 max-w-sm">{error || "You are all caught up."}</p>
      </div>
    );
  }

  const currentOrder = order;
  const employeeVoiceNotes = currentOrder.employeeNotes?.[0]?.notes || [];
  const adminVoiceNotes = currentOrder.adminNotes?.[0]?.notes || [];

  const isMasterUser = currentOrder.userEmployeeType === "Master";
  const isJobWorkUser = currentOrder.userEmployeeType === "job-work";
  const isRemarkEditableUser = isMasterUser || isJobWorkUser;
  
  const sizeSegments = currentOrder.orderDetails.map(d => d.sizeSegmentId.segmentName).join(', ');
  const totalAssignedPieces = currentOrder.orderDetails.reduce((sum, detail) => sum + detail.totalPieces, 0);

  const firstAssignment = getFirstAssignment(currentOrder);
  const isTaskAlreadyCompleted = firstAssignment?.workStatus === "completed";
  const adminRemark = firstAssignment?.remarks;


  const employeeRemarksArray: IRemarkEntry[] = (() => {
    if (!firstAssignment) return [];
    if (isMasterUser) return firstAssignment.masterRemark || [];
    if (isJobWorkUser) return firstAssignment.jobWorkRemark || [];
    return [];
})();


const RemarkHistory: React.FC<{ remarks: IRemarkEntry[] }> = ({ remarks }) => (
  <div className="mt-5 border-t pt-5">
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-semibold text-slate-700">
        Remark History <span className="text-gray-400">({remarks.length})</span>
      </h4>
    </div>

    <div className="max-h-60 overflow-y-auto pr-2 space-y-3 custom-scroll">
      {remarks.length === 0 && (
        <p className="text-xs text-gray-400 italic text-center py-4">
          No previous remarks found.
        </p>
      )}

      {remarks
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((entry, index) => (
          <div
            key={entry._id}
            className={`p-3 rounded-lg border shadow-sm transition-all ${
              index === 0
                ? "bg-slate-50 border-slate-300"
                : "bg-white border-gray-200 hover:border-slate-300"
            }`}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
              {entry.text}
            </p>

            <div className="mt-2 flex items-center justify-end">
              <span className="text-[10px] text-gray-500 tracking-wide">
                {format(new Date(entry.createdAt), "dd MMM, hh:mm a")}
              </span>
            </div>
          </div>
        ))}
    </div>
  </div>
);


  return (
    <div className="bg-slate-50 min-h-screen w-full font-sans">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-800">My Work</h1>
        <p className="text-slate-500 mt-1">Your assigned tasks</p>
      </div>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="flex flex-col gap-8">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-slate-200 rounded-lg flex-shrink-0">
                {allImages.length > 0 && <img src={allImages[0]} alt="Design" className="w-full h-full object-cover rounded-lg"/>}
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">{currentOrder.designId.basicInfo.designNo}</h3>
                <p className="text-slate-600">{currentOrder.lotNumber}</p>
                <p className="text-slate-500 text-sm">Total Assigned Pieces: <span className="font-semibold text-slate-700">{totalAssignedPieces}</span></p>
                <p className="text-slate-500 text-sm">Size Segments: <span className="font-semibold text-slate-700">{sizeSegments}</span></p>
              </div>
            </div>

            {adminRemark && (
							<div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
								<h4 className="text-sm font-bold text-indigo-700 mb-1">Admin's Assignment Note:</h4>
								<p className="text-sm text-indigo-800 italic">{adminRemark}</p>
							</div>
						)}

            <div className="my-6">
              {isMasterUser ? (
                <div className="space-y-4">
                  <h3 className="text-md font-semibold text-slate-800">Mark Completion Steps:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {(["fabric", "astar", "embroidery"] as const).map((step) => (
                      <label key={step} className={`flex items-center space-x-3 p-3 border rounded-lg bg-slate-50 has-[:checked]:bg-green-50 has-[:checked]:border-green-400 transition-colors ${isTaskAlreadyCompleted ? 'cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={masterSteps[step]}
                          onChange={() => handleMasterStepChange(step)}
                          disabled={isTaskAlreadyCompleted}
                          className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                        />
                        <span className="font-medium text-slate-700 capitalize">{step}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={handleUpdateMasterProgress}
                    disabled={isTaskAlreadyCompleted || isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 py-3 px-4 mt-4 rounded-lg font-semibold transition-all duration-300 border-2 bg-green-500 text-white border-geen-600 hover:bg-green-600 disabled:bg-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                    <span>{isTaskAlreadyCompleted ? "Task Completed" : "Mark complete"}</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button onClick={() => handleStatusClick("completed")} disabled={isTaskAlreadyCompleted} className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold transition-all duration-300 border-2 ${isTaskAlreadyCompleted ? "bg-green-600 text-white border-green-600" : "bg-white text-green-600 border-green-600 hover:bg-green-50 disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-300"}`}>
                    <Check size={20} /><span>{isTaskAlreadyCompleted ? "Completed" : "Complete Task"}</span>
                  </button>
                  <button onClick={() => handleStatusClick("incomplete")} disabled={isTaskAlreadyCompleted} className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold transition-all duration-300 border-2 ${isTaskAlreadyCompleted ? "bg-slate-400 text-white border-slate-400 cursor-not-allowed" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50 disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-300"}`}>
                    <X size={20} /><span>Mark Incomplete</span>
                  </button>
                </div>
              )}
            </div>
            {adminVoiceNotes.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center"><ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2 text-blue-600" />Admin's Instructions</h3>
                <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                  {adminVoiceNotes.slice().reverse().map((note) => (
                    <div key={note._id} className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                      <div className="flex-grow">
                        <audio src={note.url} controls className="w-full h-10"></audio>
                        <p className="text-xs text-slate-500 mt-1">
                          Recorded on: {format(new Date(note.uploadedAt), 'dd MMM yyyy, h:mm a')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isRemarkEditableUser && (
                <div className="pt-4 border-t border-gray-100">
                    <h3 className="text-md font-semibold text-slate-800 pt-4">
                        Your Progress Remark 
                        {/* ({currentOrder.userEmployeeType}): */}
                    </h3>
                    <textarea
                        value={newRemarkText}
                        onChange={(e) => setNewRemarkText(e.target.value)}
                        rows={3}
                        disabled={isTaskAlreadyCompleted || isRemarkSaving}
                        placeholder={`Write your progress notes for the admin here as a ${currentOrder.userEmployeeType}...`}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 disabled:bg-gray-100"
                    />
                    <button
                        onClick={handleSaveEmployeeRemark} // Generic handler used here
                        disabled={isTaskAlreadyCompleted || isRemarkSaving}
                        className="w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg font-semibold transition-all duration-300 border bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
                    >
                        {isRemarkSaving ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
                        <span>{isRemarkSaving ? "Saving Remark..." : "Save Remark"}</span>
                    </button>

                    {employeeRemarksArray.length > 0 && (
                        <RemarkHistory remarks={employeeRemarksArray} />
                    )}
                </div>
            )}



          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200">
            {order ? (
              <>
                <h2 className="text-xl font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">Your Voice Notes:</h2>
                <VoiceNoteRecorder
                  orderId={currentOrder._id}
                  existingNotes={employeeVoiceNotes}
                  onUpdate={fetchData}
                  uploadUrl={`/api/orders/${currentOrder._id}/upload-employee-note`}
                />
              </>
            ) : (
              <div className="text-center p-8 text-slate-500">No task assigned to record notes.</div>
            )}
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 pb-4 border-b-2 border-slate-100">Order Details</h2>
          {allImages.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-slate-500 mb-2">Design Images</p>
              <Carousel plugins={[autoplayPlugin.current]} onMouseEnter={autoplayPlugin.current.stop} onMouseLeave={autoplayPlugin.current.reset} opts={{ loop: true }} className="w-full">
                <CarouselContent>
                  {allImages.map((image, index) => (
                    <CarouselItem key={index}><div className="p-1"><img src={image} alt={`Design ${index + 1}`} className="w-full h-48 object-cover rounded-lg cursor-pointer" onClick={() => handleImageClick(index)}/></div></CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" /><CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
              </Carousel>
            </div>
          )}
          
          <div className="mb-6">
            <h4 className="text-md font-bold text-slate-800 mb-3">Taka Received</h4>
            <div className="rounded-lg border border-slate-200 overflow-hidden w-full sm:w-5/12">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100"><tr className="divide-x divide-slate-200"><th className="py-2 px-4 text-left font-semibold text-slate-700">#</th><th className="py-2 px-5 text-right font-semibold text-slate-700">Meters</th></tr></thead>
                <tbody className="divide-y divide-slate-200">
                  {currentOrder.takaArrival && currentOrder.takaArrival.length > 0 ? (
                    currentOrder.takaArrival.map((taka, index) => (
                      <tr key={taka.id} className="divide-x divide-slate-200 hover:bg-slate-50"><td className="py-2 px-4 text-slate-500">{index + 1}</td><td className="py-2 px-5 font-bold text-slate-800 text-right">{taka.meters.toLocaleString("en-IN")} m</td></tr>
                    ))
                  ) : (<tr><td colSpan={2} className="p-4 text-center text-slate-500">No Taka records found.</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {isModalOpen && (<div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"><button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-white hover:text-slate-300"><XCircle size={32} /></button><div className="relative w-full max-w-4xl h-full max-h-[80vh]"><img src={allImages[currentImageIndex]} alt={`Full-screen Design Image ${currentImageIndex + 1}`} className="w-full h-full object-contain"/>{allImages.length > 1 && (<><button onClick={prevImage} className="absolute left-0 sm:-left-12 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full"><ChevronLeft size={32} /></button><button onClick={nextImage} className="absolute right-0 sm:-right-12 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full"><ChevronRight size={32} /></button></>)}</div></div>)}
      {isConfirmModalOpen && (<div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"><div className="bg-white rounded-lg p-6 sm:p-8 m-4 max-w-sm w-full shadow-xl text-center sm:text-left"><h2 className="text-xl font-bold text-gray-800 mb-2">Confirm Action</h2><p className="text-gray-600 mb-6">Are you sure you want to mark this task as<span className={`font-bold ${actionToConfirm === "completed" ? "text-green-600" : "text-slate-700"}`}>{` ${actionToConfirm}?`}</span></p><div className="flex flex-col sm:flex-row justify-end gap-3"><button onClick={() => setIsConfirmModalOpen(false)} disabled={isSubmitting} className="px-4 py-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Cancel</button><button onClick={handleConfirmAction} disabled={isSubmitting} className={`px-4 py-2 rounded-lg text-white flex items-center justify-center ${actionToConfirm === "completed" ? "bg-green-600 hover:bg-green-700 disabled:bg-green-300" : "bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300"}`}>{isSubmitting && (<Loader2 className="animate-spin h-4 w-4 mr-2" />)}Yes, Confirm</button></div></div></div>)}
    </div>
  );
}